import type { FireGroundEffect, Position } from "@/types/game.types"
import type { Enemy } from "@/types/enemy.types"
import { generateId } from "@/utils/id-generator"
import { gameNow } from "@/utils/game-clock"

/**
 * Create a fire ground effect from siege tower projectile impact
 * @param position - Impact position
 * @param damage - Base projectile damage (fire does 15% per tick)
 * @param sourceId - Tower ID that created this effect
 * @param hasExplosiveRounds - Whether explosive rounds upgrade is unlocked (doubles damage and radius)
 */
export function createFireGroundEffect(
  position: Position,
  damage: number,
  sourceId: string,
  hasExplosiveRounds: boolean = false,
): FireGroundEffect {
  // Explosive rounds doubles the radius and damage
  const multiplier = hasExplosiveRounds ? 2.0 : 1.0
  return {
    id: generateId("fire"),
    position: { x: position.x, y: position.y },
    radius: 50 * multiplier, // 50px radius (100px with explosive rounds)
    damage: damage * 0.15 * multiplier, // 15% of projectile damage per tick (30% with explosive rounds)
    remainingDuration: 2000, // 2 seconds
    tickInterval: 250, // Damage every 250ms (4 ticks per second)
    lastTickTime: gameNow(),
    sourceId,
  }
}

/**
 * Update fire ground effects and apply damage to enemies
 * @param effects - Current fire effects
 * @param enemies - All enemies
 * @param deltaMs - Time since last frame in milliseconds
 * @returns Updated effects and map of enemy IDs to damage taken
 */
export function updateFireGroundEffects(
  effects: FireGroundEffect[],
  enemies: Enemy[],
  deltaMs: number,
): {
  effects: FireGroundEffect[]
  damagedEnemies: Map<string, number>
} {
  const now = gameNow()
  const damagedEnemies = new Map<string, number>()

  // Update remaining duration and filter out expired effects
  const updatedEffects: FireGroundEffect[] = []

  for (const effect of effects) {
    effect.remainingDuration -= deltaMs

    // Skip expired effects
    if (effect.remainingDuration <= 0) {
      continue
    }

    // Check if it's time to deal damage
    if (now - effect.lastTickTime >= effect.tickInterval) {
      effect.lastTickTime = now

      // Damage enemies in radius
      for (const enemy of enemies) {
        // Skip dead enemies
        if (enemy.health <= 0 || enemy.reachedEnd) continue

        const dx = enemy.position.x - effect.position.x
        const dy = enemy.position.y - effect.position.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist <= effect.radius + enemy.size / 2) {
          const current = damagedEnemies.get(enemy.id) || 0
          damagedEnemies.set(enemy.id, current + effect.damage)
        }
      }
    }

    updatedEffects.push(effect)
  }

  return { effects: updatedEffects, damagedEnemies }
}

/**
 * Check if any fire effects are active
 */
export function hasActiveFireEffects(effects: FireGroundEffect[]): boolean {
  return effects.length > 0
}
